import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action, notifyPropertyChange, setProperties } from '@ember/object';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import { fn } from '@ember/helper';
import isPending from '../helpers/is-pending.js';
import has from '../helpers/has.js';
import sortFn from '../helpers/sort-fn.js';
import EditUserDialog from './edit-user-dialog.gjs';
import Icon from './icon.gjs';
import PageHeader from './page-header.gjs';
import PageInfo from './page-info.gjs';
import PageContent from './page-content.gjs';
import Sortable from './sortable.gjs';
import Password from './password.gjs';
import DateTimeElapsed from './date-time-elapsed.gjs';
import {
  faUsers,
  faPlus,
  faPencil,
  faTrash,
  faKey,
  faRefresh,
  faHouseChimneyUser,
  faBuildingUser,
} from '../icons/solid.js';

function join(array) {
  return array?.join(', ') ?? '';
}

function userSortFn(sortBy) {
  const [field, order] = sortBy.split(':');

  switch (field) {
    case 'roles':
      return order === 'asc'
        ? (a, b) => join(a.roles).localeCompare(join(b.roles))
        : (a, b) => join(b.roles).localeCompare(join(a.roles));
    default:
      return sortFn(sortBy);
  }
}

export default class UsersComponent extends Component {
  @service session;
  @service time;
  @service notify;
  @service intl;
  @service requestManager;

  @tracked users = [];
  @tracked usersPromise = null;
  @tracked selectedUserIds = new Set();
  @tracked sortBy = 'username:asc';

  @tracked userDialogModel = null;
  @tracked showUserDialog = false;

  constructor() {
    super(...arguments);
    this.refreshUsers();
  }

  get selectedUsers() {
    return this.users.filter((x) => this.selectedUserIds.has(x.userId));
  }

  get allUsersSelected() {
    return this.users.length && this.selectedUsers.length === this.users.length;
  }

  get someUsersSelected() {
    return (
      this.selectedUsers.length && this.selectedUsers.length < this.users.length
    );
  }

  get sortedUsers() {
    return this.users.sort(userSortFn(this.sortBy));
  }

  @action async refreshUsers() {
    this.usersPromise = this.getUsers();
  }

  @action async getUsers() {
    const { content } = await this.requestManager.request({
      url: '/api/users',
    });
    this.users = content;
  }

  @action select(user) {
    if (this.selectedUserIds.has(user.userId)) {
      this.selectedUserIds.delete(user.userId);
    } else {
      this.selectedUserIds.add(user.userId);
    }
    notifyPropertyChange(this, 'selectedUserIds');
  }

  @action selectAll() {
    if (this.allUsersSelected) {
      this.selectedUserIds.clear();
    } else {
      this.users.forEach((x) => this.selectedUserIds.add(x.userId));
    }
    notifyPropertyChange(this, 'selectedUserIds');
  }

  @action newUser() {
    this.userDialogModel = null;
    this.showUserDialog = true;
  }

  @action editUser(user) {
    this.userDialogModel = user;
    this.showUserDialog = true;
  }

  @action async createUser(attributes) {
    try {
      this.usersPromise = this.requestManager.request({
        url: '/api/users',
        method: 'POST',
        headers: new Headers([['Content-Type', 'application/json']]),
        body: JSON.stringify(attributes),
      });
      const { content } = await this.usersPromise;
      this.users = [...this.users, content];
      this.notify.alert(this.intl.t('users.create-success'));
    } catch (error) {
      this.notify.alert(this.intl.t('users.create-error'), error);
    }
  }

  @action async updateUser(attributes) {
    const user = this.userDialogModel;
    setProperties(user, attributes);
    try {
      this.usersPromise = this.requestManager.request({
        url: `/api/users/${user.userId}`,
        method: 'PUT',
        headers: new Headers([['Content-Type', 'application/json']]),
        body: JSON.stringify(attributes),
      });
      const { content } = await this.usersPromise;
      setProperties(user, content);
      this.notify.alert(this.intl.t('users.update-success'));
    } catch (error) {
      this.notify.alert(this.intl.t('users.update-error'), error);
      this.refreshUsers();
    }
  }

  @action async deleteUsers() {
    for (const user of this.selectedUsers) {
      try {
        this.usersPromise = this.requestManager.request({
          url: `/api/users/${user.userId}`,
          method: 'DELETE',
        });

        await this.usersPromise;

        this.users = this.users.filter((x) => x.userId !== user.userId);
        this.selectedUserIds.delete(user.userId);
        notifyPropertyChange(this, 'selectedUserIds');
      } catch (error) {
        this.notify.alert(
          this.intl.t('users.delete-error', { username: user.username }),
          error,
        );
        this.refreshUsers();
        return;
      }
    }

    this.notify.alert(this.intl.t('users.delete-success'));
  }

  @action async resetPasswords() {
    for (const user of this.selectedUsers) {
      try {
        this.usersPromise = this.requestManager.request({
          url: `/api/users/${user.userId}/reset-password`,
          method: 'POST',
        });

        const { content } = await this.usersPromise;
        setProperties(user, content);

        this.selectedUserIds.delete(user.userId);
        notifyPropertyChange(this, 'selectedUserIds');
      } catch (error) {
        this.notify.alert(
          this.intl.t('users.reset-password-error', {
            username: user.username,
          }),
          error,
        );
        return;
      }
    }

    this.notify.alert(this.intl.t('users.reset-password-success'));
  }

  <template>
    <PageHeader
      @icon={{faUsers}}
      @title={{t "users.title"}}
      @promise={{this.usersPromise}}
      @onClose={{@onClose}}
    />

    <PageInfo>
      {{t "users.description"}}
    </PageInfo>

    <PageContent>
      <:tools>
        {{#if this.selectedUsers.length}}
          <button
            type="button"
            class="btn btn-ghost text-primary uppercase"
            {{on "click" this.resetPasswords}}
          >
            <Icon @icon={{faKey}} />
            {{t "users.reset-password"}}
            <span class="badge badge-sm">
              {{this.selectedUsers.length}}
            </span>
          </button>

          <button
            type="button"
            class="btn btn-ghost text-primary uppercase"
            {{on "click" this.deleteUsers}}
          >
            <Icon @icon={{faTrash}} />
            {{t "delete"}}
            <span class="badge badge-sm">
              {{this.selectedUsers.length}}
            </span>
          </button>
        {{else}}
          <button
            type="button"
            class="btn btn-ghost text-primary uppercase"
            {{on "click" this.newUser}}
          >
            <Icon @icon={{faPlus}} />
            {{t "users.new"}}
          </button>
        {{/if}}

        <div class="flex-1" />

        <button
          type="button"
          class="btn btn-ghost btn-square text-accent tooltip tooltip-left"
          data-tip={{t "refresh"}}
          {{on "click" this.refreshUsers}}
        >
          <Icon @icon={{faRefresh}} />
        </button>
      </:tools>

      <:content>
        <table class="table table-pin-rows table-xs">
          <thead>
            <tr>
              <th class="w-10">
                <input
                  type="checkbox"
                  aria-label={{t "select-all"}}
                  class="checkbox checkbox-primary"
                  disabled={{not this.users.length}}
                  checked={{this.allUsersSelected}}
                  indeterminate={{this.someUsersSelected}}
                  {{on "click" this.selectAll}}
                />
              </th>
              <th class="w-96">
                <Sortable
                  @value={{this.sortBy}}
                  @onChange={{fn (mut this.sortBy)}}
                  @name="username"
                >
                  {{t "users.username"}}
                </Sortable>
              </th>
              <th class="w-10"></th>
              <th class="w-48">
                <Sortable
                  @value={{this.sortBy}}
                  @onChange={{fn (mut this.sortBy)}}
                  @name="roles"
                >
                  {{t "users.roles"}}
                </Sortable>
              </th>
              <th class="w-48">
                <Sortable
                  @value={{this.sortBy}}
                  @onChange={{fn (mut this.sortBy)}}
                  @name="isLocal"
                >
                  {{t "users.type"}}
                </Sortable>
              </th>
              <th class="w-96">
                <Sortable
                  @value={{this.sortBy}}
                  @onChange={{fn (mut this.sortBy)}}
                  @name="created"
                >
                  {{t "created"}}
                </Sortable>
              </th>
              <th class="w-64">
                <Sortable
                  @value={{this.sortBy}}
                  @onChange={{fn (mut this.sortBy)}}
                  @name="password"
                >
                  {{t "users.password"}}
                </Sortable>
              </th>
              <th class="w-96">
                <Sortable
                  @value={{this.sortBy}}
                  @onChange={{fn (mut this.sortBy)}}
                  @name="passwordModified"
                >
                  {{t "users.password-modified"}}
                </Sortable>
              </th>
            </tr>
          </thead>
          <tbody>
            {{#each this.sortedUsers key="userId" as |user|}}
              <tr class="hover">
                <th>
                  <input
                    type="checkbox"
                    aria-label={{t "select"}}
                    class="checkbox checkbox-primary"
                    checked={{has this.selectedUserIds user.userId}}
                    {{on "click" (fn this.select user)}}
                  />
                </th>
                <td class="text-base font-bold text-primary">
                  {{user.username}}
                </td>

                <td>
                  {{#if user.isLocal}}
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm text-primary show-on-hover"
                      {{on "click" (fn this.editUser user)}}
                    >
                      <Icon @icon={{faPencil}} />
                    </button>
                  {{/if}}
                </td>

                <td class="text-sm">
                  {{join user.roles}}
                </td>

                <td class="text-sm">
                  <div class="flex items-center">
                    <Icon
                      @icon={{if
                        user.isLocal
                        faHouseChimneyUser
                        faBuildingUser
                      }}
                      @size="1x"
                      @class="opacity-80 w-5 mr-1"
                    />
                    <div>
                      {{t (if user.isLocal "users.local" "users.external")}}
                    </div>
                  </div>
                </td>

                <td class="text-sm">
                  <DateTimeElapsed @value={{user.created}} />
                </td>

                {{#if user.passwordModified}}
                  <td class="text-sm">
                    <Password @value={{user.password}} />
                  </td>
                  <td class="text-sm">
                    <DateTimeElapsed @value={{user.passwordModified}} />
                  </td>
                {{else}}
                  <td colspan="2" class="text-sm">
                    {{t "users.password-not-created"}}
                  </td>
                {{/if}}
              </tr>
            {{else}}
              {{#unless (isPending this.usersPromise)}}
                <tr>
                  <td colspan="3" class="text-sm">
                    {{t "empty-folder-message"}}
                  </td>
                </tr>
              {{/unless}}
            {{/each}}
          </tbody>
        </table>
      </:content>
    </PageContent>

    {{#if this.showUserDialog}}
      <EditUserDialog
        @model={{this.userDialogModel}}
        @users={{this.users}}
        @onSubmit={{if this.userDialogModel this.updateUser this.createUser}}
        @onClose={{fn (mut this.showUserDialog) false}}
      />
    {{/if}}
  </template>
}
